import defaultLogoUrl from "@/shared/assets/NectarPayKioskFlat.png";
import defaultIconUrl from "@/shared/assets/NectarPayKioskFlat.png";
import type { KioskPublicBrandingResponse, KioskResolvedBranding } from "@/kiosk/types/kiosk";

export const DEFAULT_KIOSK_PRIMARY_COLOR = "#FF6F00";
export const DEFAULT_KIOSK_SECONDARY_COLOR = "#5c5c5c";
export const DEFAULT_KIOSK_BUSINESS_NAME = "NectarPay";
export const DEFAULT_KIOSK_LOGO_URL = defaultLogoUrl;
export const DEFAULT_KIOSK_ICON_URL = defaultIconUrl;

const THREE_DIGIT_HEX_COLOR = /^#([0-9a-f]{3})$/i;
const SIX_DIGIT_HEX_COLOR = /^#([0-9a-f]{6})$/i;

export const normalizeHexColor = (value: string | null | undefined): string | null => {
  if (typeof value !== "string") {
    return null;
  }

  const trimmedValue = value.trim();
  const shortMatch = trimmedValue.match(THREE_DIGIT_HEX_COLOR);
  if (shortMatch) {
    const [red, green, blue] = shortMatch[1].split("");
    return `#${red}${red}${green}${green}${blue}${blue}`.toUpperCase();
  }

  if (SIX_DIGIT_HEX_COLOR.test(trimmedValue)) {
    return trimmedValue.toUpperCase();
  }

  return null;
};

const clampChannel = (value: number): number => Math.max(0, Math.min(255, value));

const hexToChannels = (hexColor: string): [number, number, number] => {
  const normalizedHex = normalizeHexColor(hexColor) ?? DEFAULT_KIOSK_PRIMARY_COLOR;

  return [0, 2, 4].map((offset) =>
    Number.parseInt(normalizedHex.slice(offset + 1, offset + 3), 16),
  ) as [number, number, number];
};

export const lightenHexColor = (hexColor: string, amount = 0.18): string => {
  const [red, green, blue] = hexToChannels(hexColor);
  const nextChannels = [red, green, blue].map((channel) =>
    clampChannel(Math.round(channel + (255 - channel) * amount)),
  );

  return `#${nextChannels
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
};

export const hexColorToRgbTuple = (hexColor: string): string => {
  const [red, green, blue] = hexToChannels(hexColor);
  return `${red}, ${green}, ${blue}`;
};

const getRelativeLuminance = (channel: number): number => {
  const normalized = channel / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
};

export const getContrastingHexColor = (hexColor: string): string => {
  const [red, green, blue] = hexToChannels(hexColor);
  const luminance =
    getRelativeLuminance(red) * 0.2126 +
    getRelativeLuminance(green) * 0.7152 +
    getRelativeLuminance(blue) * 0.0722;

  return luminance > 0.55 ? "#0F172A" : "#FFFFFF";
};

export const resolveKioskBranding = (
  branding?: Partial<KioskPublicBrandingResponse> | null,
): KioskResolvedBranding => {
  const primaryColor =
    normalizeHexColor(branding?.primary_color ?? null) ?? DEFAULT_KIOSK_PRIMARY_COLOR;
  const secondaryColor =
    normalizeHexColor(branding?.secondary_color ?? null) ?? DEFAULT_KIOSK_SECONDARY_COLOR;

  const businessName =
    typeof branding?.business_name === "string" && branding.business_name.trim().length > 0
      ? branding.business_name.trim()
      : DEFAULT_KIOSK_BUSINESS_NAME;
  const displayTitle =
    typeof branding?.title === "string" && branding.title.trim().length > 0
      ? branding.title.trim()
      : businessName;
  const displaySubtitle =
    typeof branding?.subtitle === "string" && branding.subtitle.trim().length > 0
      ? branding.subtitle.trim()
      : "";

  return {
    primaryColor,
    primaryColorLight: lightenHexColor(primaryColor),
    secondaryColor,
    logoUrl:
      typeof branding?.logo === "string" && branding.logo.trim().length > 0
        ? branding.logo
        : DEFAULT_KIOSK_LOGO_URL,
    iconUrl:
      typeof branding?.icon === "string" && branding.icon.trim().length > 0
        ? branding.icon
        : DEFAULT_KIOSK_ICON_URL,
    businessName,
    displayTitle,
    displaySubtitle,
  };
};

export const buildKioskDocumentTitle = (
  pageTitle: string,
  displayTitle = DEFAULT_KIOSK_BUSINESS_NAME,
): string => `${displayTitle} | ${pageTitle}`;

export const buildKioskAppleAppTitle = (displayTitle = DEFAULT_KIOSK_BUSINESS_NAME): string =>
  `${displayTitle} Kiosk`;
